import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Save as SaveIcon, SwapHoriz as SwapHorizIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import { ROUTES } from '../../config/routes';
import crmActividadService from '../../services/crmActividadService';
import crmCatalogoService from '../../services/crmCatalogoService';
import crmContactoService from '../../services/crmContactoService';
import crmEmpresaService from '../../services/crmEmpresaService';
import type {
  CrmActividad,
  CrmContacto,
  CrmEmpresaListItem,
  CrmOrigen,
  CrmTag,
} from '../../types/crm';
import { getApiErrorMessage } from '../../utils/apiError';
import CrmTimeline from './components/CrmTimeline';

const emptyForm = {
  nombre: '',
  empresaId: null as number | null,
  cargo: '',
  telefono: '',
  email: '',
  origenId: '' as number | '',
  nroVendedor: null as number | null,
  nroCliente: '',
  activo: true,
};

const CrmContactoForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'nuevo';
  const contactoId = isNew ? null : Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(emptyForm);
  const [selectedTags, setSelectedTags] = useState<CrmTag[]>([]);
  const [nuevosTags, setNuevosTags] = useState<string[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [origenes, setOrigenes] = useState<CrmOrigen[]>([]);
  const [empresas, setEmpresas] = useState<CrmEmpresaListItem[]>([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState<CrmEmpresaListItem | null>(null);
  const [busquedaEmpresa, setBusquedaEmpresa] = useState('');
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [actividades, setActividades] = useState<CrmActividad[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingActs, setLoadingActs] = useState(false);
  const [reasignarOpen, setReasignarOpen] = useState(false);
  const [nroVendedorNuevo, setNroVendedorNuevo] = useState<number | null>(null);
  const [notaReasignar, setNotaReasignar] = useState('');
  const [detalle, setDetalle] = useState<CrmContacto | null>(null);
  const [empresaDialogOpen, setEmpresaDialogOpen] = useState(false);
  const [nuevaEmpresa, setNuevaEmpresa] = useState({ nombre: '', cuit: '', telefono: '', email: '' });
  const [savingEmpresa, setSavingEmpresa] = useState(false);
  const [origenDialogOpen, setOrigenDialogOpen] = useState(false);
  const [nuevoOrigenNombre, setNuevoOrigenNombre] = useState('');
  const [savingOrigen, setSavingOrigen] = useState(false);

  const loadActividades = async (cid: number) => {
    try {
      setLoadingActs(true);
      const data = await crmActividadService.getAll({ contactoId: cid });
      setActividades(Array.isArray(data) ? data : []);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar actividades'), { variant: 'error' });
      setActividades([]);
    } finally {
      setLoadingActs(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [o, t] = await Promise.all([
          crmCatalogoService.getOrigenes(),
          crmCatalogoService.getTags(),
        ]);
        setOrigenes(o);
        setTags(t);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar catálogos'), { variant: 'error' });
      }
    })();
  }, [enqueueSnackbar]);

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        setLoadingEmpresas(true);
        const data = await crmEmpresaService.getAll({
          searchTerm: busquedaEmpresa || undefined,
          pageNumber: 1,
          pageSize: 30,
          activo: true,
        });
        setEmpresas(data.items ?? []);
      } catch {
        setEmpresas([]);
      } finally {
        setLoadingEmpresas(false);
      }
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [busquedaEmpresa]);

  useEffect(() => {
    if (contactoId == null || Number.isNaN(contactoId)) return;
    (async () => {
      try {
        setLoading(true);
        const c = await crmContactoService.getById(contactoId);
        setDetalle(c);
        setForm({
          nombre: c.nombre ?? '',
          empresaId: c.empresaId ?? null,
          cargo: c.cargo ?? '',
          telefono: c.telefono ?? '',
          email: c.email ?? '',
          origenId: c.origenId ?? '',
          nroVendedor: c.nroVendedor ?? null,
          nroCliente: c.nroCliente != null ? String(c.nroCliente) : '',
          activo: c.activo,
        });
        if (c.empresaId) {
          const seleccionada: CrmEmpresaListItem = {
            id: c.empresaId,
            nombre: c.empresa ?? `Empresa #${c.empresaId}`,
            activo: true,
            cantidadContactos: 0,
            fechaAlta: c.fechaAlta,
          };
          setEmpresaSeleccionada(seleccionada);
          setBusquedaEmpresa(seleccionada.nombre);
        } else if (c.empresa) {
          setEmpresaSeleccionada(null);
          setBusquedaEmpresa(c.empresa);
        } else {
          setEmpresaSeleccionada(null);
          setBusquedaEmpresa('');
        }
        setSelectedTags(c.tags ?? []);
        await loadActividades(contactoId);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar el contacto'), { variant: 'error' });
        navigate(ROUTES.crm.contactos.list);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactoId]);

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      enqueueSnackbar('El nombre es obligatorio', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        empresaId: form.empresaId,
        empresa: form.empresaId ? null : busquedaEmpresa.trim() || null,
        cargo: form.cargo.trim() || null,
        telefono: form.telefono.trim() || null,
        email: form.email.trim() || null,
        origenId: form.origenId === '' ? null : form.origenId,
        nroVendedor: form.nroVendedor,
        nroCliente: form.nroCliente.trim() ? Number(form.nroCliente) : null,
        tagIds: selectedTags.map((t) => t.id),
        nuevosTags: nuevosTags.length ? nuevosTags : null,
      };

      if (isNew) {
        const created = await crmContactoService.create(payload);
        enqueueSnackbar('Contacto creado', { variant: 'success' });
        navigate(ROUTES.crm.contactos.editar(created.id), { replace: true });
      } else if (contactoId != null) {
        await crmContactoService.update(contactoId, { ...payload, activo: form.activo });
        enqueueSnackbar('Contacto actualizado', { variant: 'success' });
        const refreshed = await crmContactoService.getById(contactoId);
        setDetalle(refreshed);
        setSelectedTags(refreshed.tags ?? []);
        setNuevosTags([]);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar el contacto'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReasignar = async () => {
    if (contactoId == null || nroVendedorNuevo == null) {
      enqueueSnackbar('Seleccioná el nuevo vendedor', { variant: 'warning' });
      return;
    }
    try {
      const updated = await crmContactoService.reasignar(contactoId, {
        nroVendedorNuevo,
        nota: notaReasignar.trim() || null,
      });
      setDetalle(updated);
      setForm((f) => ({ ...f, nroVendedor: updated.nroVendedor }));
      setReasignarOpen(false);
      setNotaReasignar('');
      enqueueSnackbar('Contacto reasignado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al reasignar'), { variant: 'error' });
    }
  };

  const openNuevaEmpresa = () => {
    setNuevaEmpresa({
      nombre: busquedaEmpresa.trim(),
      cuit: '',
      telefono: '',
      email: '',
    });
    setEmpresaDialogOpen(true);
  };

  const handleCrearEmpresa = async () => {
    if (!nuevaEmpresa.nombre.trim()) {
      enqueueSnackbar('El nombre de la empresa es obligatorio', { variant: 'warning' });
      return;
    }
    setSavingEmpresa(true);
    try {
      const created = await crmEmpresaService.create({
        nombre: nuevaEmpresa.nombre.trim(),
        cuit: nuevaEmpresa.cuit.trim() || null,
        telefono: nuevaEmpresa.telefono.trim() || null,
        email: nuevaEmpresa.email.trim() || null,
      });
      const item: CrmEmpresaListItem = {
        id: created.id,
        nombre: created.nombre,
        cuit: created.cuit,
        telefono: created.telefono,
        email: created.email,
        direccion: created.direccion,
        sitioWeb: created.sitioWeb,
        nroCliente: created.nroCliente,
        nombreCliente: created.nombreCliente,
        activo: created.activo,
        cantidadContactos: created.cantidadContactos,
        fechaAlta: created.fechaAlta,
      };
      setEmpresas((prev) => [item, ...prev.filter((e) => e.id !== item.id)]);
      setEmpresaSeleccionada(item);
      setForm((f) => ({ ...f, empresaId: item.id }));
      setBusquedaEmpresa(item.nombre);
      setEmpresaDialogOpen(false);
      enqueueSnackbar('Empresa creada', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear la empresa'), { variant: 'error' });
    } finally {
      setSavingEmpresa(false);
    }
  };

  const handleCrearOrigen = async () => {
    if (!nuevoOrigenNombre.trim()) {
      enqueueSnackbar('El nombre del origen es obligatorio', { variant: 'warning' });
      return;
    }
    setSavingOrigen(true);
    try {
      const created = await crmCatalogoService.createOrigen({
        nombre: nuevoOrigenNombre.trim(),
      });
      setOrigenes((prev) => [...prev, created].sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre)));
      setForm((f) => ({ ...f, origenId: created.id }));
      setOrigenDialogOpen(false);
      setNuevoOrigenNombre('');
      enqueueSnackbar('Origen creado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear el origen'), { variant: 'error' });
    } finally {
      setSavingOrigen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(ROUTES.crm.contactos.list)}
                >
                  Volver
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {isNew ? 'Nuevo contacto' : 'Editar contacto'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {!isNew && (
                  <Can permiso="crm.contactos.reasignar">
                    <Button
                      variant="outlined"
                      startIcon={<SwapHorizIcon />}
                      onClick={() => {
                        setNroVendedorNuevo(null);
                        setReasignarOpen(true);
                      }}
                    >
                      Reasignar
                    </Button>
                  </Can>
                )}
                <Can permiso={isNew ? 'crm.contactos.nuevo' : 'crm.contactos.editar'}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    Guardar
                  </Button>
                </Can>
              </Stack>
            </Stack>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                />
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                  <Autocomplete
                    fullWidth
                    options={empresas}
                    loading={loadingEmpresas}
                    value={empresaSeleccionada}
                    inputValue={busquedaEmpresa}
                    onChange={(_, value) => {
                      setEmpresaSeleccionada(value);
                      setForm((f) => ({ ...f, empresaId: value?.id ?? null }));
                      setBusquedaEmpresa(value?.nombre ?? '');
                    }}
                    getOptionLabel={(opt) => opt.nombre}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterOptions={(x) => x}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setBusquedaEmpresa(value);
                        setEmpresaSeleccionada(null);
                        setForm((f) => ({ ...f, empresaId: null }));
                      } else if (reason === 'clear') {
                        setBusquedaEmpresa('');
                        setEmpresaSeleccionada(null);
                        setForm((f) => ({ ...f, empresaId: null }));
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Empresa" />}
                  />
                  <Can permiso="crm.empresas.nuevo">
                    <IconButton
                      color="primary"
                      onClick={openNuevaEmpresa}
                      title="Nueva empresa"
                      aria-label="Nueva empresa"
                      sx={{ mt: 0.5 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Can>
                </Stack>
                <TextField
                  label="Cargo"
                  fullWidth
                  value={form.cargo}
                  onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Teléfono"
                  fullWidth
                  value={form.telefono}
                  onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                />
                <TextField
                  label="Email"
                  fullWidth
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                  <TextField
                    select
                    label="Origen"
                    fullWidth
                    value={form.origenId}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        origenId: e.target.value === '' ? '' : Number(e.target.value),
                      }))
                    }
                  >
                    <MenuItem value="">Sin origen</MenuItem>
                    {origenes.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Can permiso={isNew ? 'crm.contactos.nuevo' : 'crm.contactos.editar'}>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        setNuevoOrigenNombre('');
                        setOrigenDialogOpen(true);
                      }}
                      title="Nuevo origen"
                      aria-label="Nuevo origen"
                      sx={{ mt: 0.5 }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Can>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                <VendedorSelect
                  value={form.nroVendedor}
                  onChange={(v) => setForm((f) => ({ ...f, nroVendedor: v }))}
                  allowEmpty
                  emptyOptionLabel="Sin vendedor"
                  nombreVendedorFallback={detalle?.nombreVendedor}
                />
                <TextField
                  label="N° Cliente"
                  fullWidth
                  value={form.nroCliente}
                  onChange={(e) => setForm((f) => ({ ...f, nroCliente: e.target.value }))}
                  helperText="Dejá vacío si es prospecto"
                />
                {!isNew && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.activo}
                        onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
                      />
                    }
                    label="Activo"
                    sx={{ mt: 1 }}
                  />
                )}
              </Stack>
              <Autocomplete
                multiple
                freeSolo
                options={tags}
                value={[...selectedTags, ...nuevosTags]}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.nombre)}
                isOptionEqualToValue={(a, b) => {
                  if (typeof a === 'string' || typeof b === 'string') {
                    return String(typeof a === 'string' ? a : a.nombre) ===
                      String(typeof b === 'string' ? b : b.nombre);
                  }
                  return a.id === b.id;
                }}
                onChange={(_, value) => {
                  const existing: CrmTag[] = [];
                  const created: string[] = [];
                  value.forEach((v) => {
                    if (typeof v === 'string') {
                      const trimmed = v.trim();
                      if (trimmed) created.push(trimmed);
                    } else {
                      existing.push(v);
                    }
                  });
                  setSelectedTags(existing);
                  setNuevosTags(created);
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={typeof option === 'string' ? option : option.id}
                      label={typeof option === 'string' ? option : option.nombre}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    helperText="Seleccioná existentes o escribí uno nuevo y Enter"
                  />
                )}
              />
            </Stack>

            {!isNew && contactoId != null && (
              <>
                <Divider />
                <CrmTimeline
                  actividades={actividades}
                  loading={loadingActs}
                  canEdit
                  onCreate={async (dto) => {
                    try {
                      await crmActividadService.create({ ...dto, contactoId });
                      enqueueSnackbar('Actividad agregada', { variant: 'success' });
                      await loadActividades(contactoId);
                    } catch (error) {
                      enqueueSnackbar(getApiErrorMessage(error, 'Error al crear actividad'), {
                        variant: 'error',
                      });
                      throw error;
                    }
                  }}
                  onToggleEstado={async (actividad) => {
                    try {
                      await crmActividadService.setEstado(actividad.id, !actividad.completada);
                      enqueueSnackbar(
                        actividad.completada
                          ? 'Actividad marcada como pendiente'
                          : 'Actividad marcada como completada',
                        { variant: 'success' },
                      );
                      await loadActividades(contactoId);
                    } catch (error) {
                      enqueueSnackbar(getApiErrorMessage(error, 'Error al cambiar estado'), {
                        variant: 'error',
                      });
                      throw error;
                    }
                  }}
                />

                {(detalle?.reasignaciones?.length ?? 0) > 0 && (
                  <>
                    <Divider />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Historial de reasignaciones
                    </Typography>
                    <Stack spacing={1}>
                      {detalle?.reasignaciones.map((r) => (
                        <Typography key={r.id} variant="body2" color="text.secondary">
                          {new Date(r.fecha).toLocaleString('es-AR')} — vend. {r.nroVendedorAnterior} →{' '}
                          {r.nroVendedorNuevo} ({r.usuario})
                          {r.nota ? `: ${r.nota}` : ''}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                )}
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={reasignarOpen} onClose={() => setReasignarOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Reasignar contacto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <VendedorSelect
              value={nroVendedorNuevo}
              onChange={setNroVendedorNuevo}
              label="Nuevo vendedor"
              required
            />
            <TextField
              label="Nota"
              fullWidth
              multiline
              minRows={2}
              value={notaReasignar}
              onChange={(e) => setNotaReasignar(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasignarOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleReasignar}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={empresaDialogOpen} onClose={() => setEmpresaDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nueva empresa</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              required
              fullWidth
              autoFocus
              value={nuevaEmpresa.nombre}
              onChange={(e) => setNuevaEmpresa((f) => ({ ...f, nombre: e.target.value }))}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="CUIT"
                fullWidth
                value={nuevaEmpresa.cuit}
                onChange={(e) => setNuevaEmpresa((f) => ({ ...f, cuit: e.target.value }))}
              />
              <TextField
                label="Teléfono"
                fullWidth
                value={nuevaEmpresa.telefono}
                onChange={(e) => setNuevaEmpresa((f) => ({ ...f, telefono: e.target.value }))}
              />
            </Stack>
            <TextField
              label="Email"
              fullWidth
              value={nuevaEmpresa.email}
              onChange={(e) => setNuevaEmpresa((f) => ({ ...f, email: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmpresaDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCrearEmpresa} disabled={savingEmpresa}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={origenDialogOpen} onClose={() => setOrigenDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nuevo origen</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            required
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            value={nuevoOrigenNombre}
            onChange={(e) => setNuevoOrigenNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCrearOrigen();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrigenDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCrearOrigen} disabled={savingOrigen}>
            Crear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmContactoForm;
